import React from 'react';
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Paper
} from '@mui/material';

// Small helper to keep dense/compact look
const denseSx = {
  '& .MuiInputBase-root': { height: 30, fontSize: 12 },
  '& .MuiInputBase-input': { p: '5px 8px', textAlign: 'right' },
  '& .MuiFormLabel-root': { fontSize: 12, transform: 'translate(8px, 7px) scale(1)' },
  '& .MuiInputLabel-shrink': { transform: 'translate(8px, -9px) scale(0.85)' },
};

// Currency formatter (India locale). Adjust as needed.
const fmt = (v) =>
  v === '' || v === null || v === undefined
    ? ''
    : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/**
 * Props:
 * - values: object with all numeric fields (strings or numbers)
 * - onChange: (name, value) => void
 * - readOnly: boolean to lock fields
 *
 * Fields used:
 * left column:
 *  totalQtyKg, grossAmount, discountAmount, totalAmount,
 *  insuranceRatePct, insuranceAmount, freightAmountPerMT, freightAmount
 * right column:
 *  serviceChargePct, sChargeAmount, taxableAmount, cessAmount,
 *  taxAmount, roundOff, netAmount
 */
const SummaryLedger = ({
  values = {},
  onChange = () => {},
  readOnly = false,
}) => {
  // generic change handler
  const handleNumChange = (name) => (e) => {
    const raw = e.target.value;
    onChange(name, raw);
  };

  // label cell style (mimics flat header look)
  const labelBox = (text, helperRight = '') => (
    <Box
      sx={{
        height: 30,
        display: 'flex',
        alignItems: 'center',
        px: 1,
        fontSize: 12,
        bgcolor: 'action.disabledBackground',
        color: 'text.primary',
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRight: 0,
        borderRadius: '4px 0 0 4px',
        justifyContent: 'space-between',
      }}
    >
      <Typography component="span" sx={{ fontSize: 12 }}>{text}</Typography>
      {helperRight ? (
        <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', ml: 1 }}>
          {helperRight}
        </Typography>
      ) : null}
    </Box>
  );

  const inputCell = (name, { unit = '₹', width = '100%', disabled = false } = {}) => (
    <TextField
      size="small"
      value={values[name] ?? ''}
      onChange={handleNumChange(name)}
      disabled={readOnly || disabled}
      variant="outlined"
      fullWidth
      sx={{
        ...denseSx,
        '& .MuiOutlinedInput-root': {
          borderRadius: '0 4px 4px 0',
        },
      }}
      InputProps={{
        startAdornment:
          unit && (unit === 'Rate' || unit === 'Amount')
            ? <InputAdornment position="start" sx={{ fontSize: 11 }}>{unit}</InputAdornment>
            : undefined,
        endAdornment:
          unit && (unit === '₹' || unit === '%')
            ? <InputAdornment position="end" sx={{ fontSize: 11 }}>{unit}</InputAdornment>
            : undefined,
        inputMode: 'decimal',
      }}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.default' }}>
      <Grid container spacing={0.5}>
        {/* Left column */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={0.5}>
            <Grid item xs={6}>{labelBox('Total Qty (in Kgs)')}</Grid>
            <Grid item xs={6}>{inputCell('totalQtyKg', { unit: '' })}</Grid>

            <Grid item xs={6}>{labelBox('Gross Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('grossAmount', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Discount Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('discountAmount', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Total Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('totalAmount', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Insurance Rate', 'Rate')}</Grid>
            <Grid item xs={6}>{inputCell('insuranceRatePct', { unit: '%'} )}</Grid>

            <Grid item xs={6}>{labelBox('Insurance Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('insuranceAmount', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Freight Amount / MT', 'Amount')}</Grid>
            <Grid item xs={6}>{inputCell('freightAmountPerMT', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Freight Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('freightAmount', { unit: '₹' })}</Grid>

            
          </Grid>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={0.5}>
            <Grid item xs={6}>{labelBox('Service Charge %')}</Grid>
            <Grid item xs={6}>{inputCell('serviceChargePct', { unit: '%' })}</Grid>

            <Grid item xs={6}>{labelBox('S.Charge Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('sChargeAmount', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Taxable Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('taxableAmount', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Cess Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('cessAmount', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Tax Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('taxAmount', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Round Off', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('roundOff', { unit: '₹' })}</Grid>

            <Grid item xs={6}>{labelBox('Net Amount', '₹')}</Grid>
            <Grid item xs={6}>{inputCell('netAmount', { unit: '₹' })}</Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SummaryLedger;
